"use client"

import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { MapPin, DollarSign, ArrowLeftRight, Circle, Pentagon } from "lucide-react"
import { RouteAreaRate } from "@/utils/routeAreaRate"

interface Location {
  id?: number;
  text: string;
  lat: number;
  lng: number;
}

interface FixedRatesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onRateSelect: (rate: RouteAreaRate) => void
  rates: RouteAreaRate[]
  pickupLocation?: Location
  dropoffLocation?: Location | null
}

export const FixedRatesDialog: React.FC<FixedRatesDialogProps> = ({
  open,
  onOpenChange,
  onRateSelect,
  rates,
  pickupLocation,
  dropoffLocation,
}) => {

  // Calculate distance between two points in kilometers
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get center point of an area
  const getAreaCenter = (coordinates: any[], type: string) => {
    if (!coordinates || coordinates.length === 0) return null;
    
    if (type === "POINT") {
      return { lat: coordinates[0].lat, lng: coordinates[0].lng };
    }
    
    if (type === "RADIUS") {
      return { lat: coordinates[0].lat, lng: coordinates[0].lng };
    }
    
    // For AREA (polygon), calculate centroid
    if (type === "AREA") {
      const latSum = coordinates.reduce((sum, coord) => sum + coord.lat, 0);
      const lngSum = coordinates.reduce((sum, coord) => sum + coord.lng, 0);
      return {
        lat: latSum / coordinates.length,
        lng: lngSum / coordinates.length
      };
    }
    
    return null;
  };

  // Check if a rate is within 10km of pickup or dropoff locations
  const isRateNearby = (rate: RouteAreaRate): boolean => {
    if (!pickupLocation && !dropoffLocation) return true; // Show all if no locations selected
    
    const pickupAreaCenter = getAreaCenter(rate.pickup_area.coordinates, rate.pickup_area.type);
    const dropoffAreaCenter = getAreaCenter(rate.dropoff_area.coordinates, rate.dropoff_area.type);
    
    if (!pickupAreaCenter || !dropoffAreaCenter) return false;
    
    let isPickupNearby = false;
    let isDropoffNearby = false;

    // Check if pickup area is within 10km of selected pickup location
    if (pickupLocation) {
      const pickupDistance = calculateDistance(
        pickupLocation.lat, pickupLocation.lng,
        pickupAreaCenter.lat, pickupAreaCenter.lng
      );
      if (pickupDistance <= 10) isPickupNearby = true;
    }
    
    // Check if dropoff area is within 10km of selected dropoff location
    if (dropoffLocation) {
      const dropoffDistance = calculateDistance(
        dropoffLocation.lat, dropoffLocation.lng,
        dropoffAreaCenter.lat, dropoffAreaCenter.lng
      );
      if (dropoffDistance <= 10) isDropoffNearby = true;
    }

    return isPickupNearby && isDropoffNearby;
  };

  // Filter rates to show only those within 10km
  const filteredRates = rates.filter(isRateNearby);

  const getAreaTypeIcon = (type: string) => {
    switch (type) {
      case "AREA":
        return <Pentagon className="h-3 w-3" />
      case "RADIUS":
        return <Circle className="h-3 w-3" />
      case "POINT":
      default:
        return <MapPin className="h-3 w-3" />
    }
  }

  const handleRateSelect = (rate: RouteAreaRate) => {
    onRateSelect(rate)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Select Fixed Rate
          </DialogTitle>
          <DialogDescription>
            Only rates within 10km of your selected pickup or dropoff locations are shown.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {filteredRates.length === 0 && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <p>
                {rates.length === 0 
                  ? "No fixed rates configured" 
                  : "No fixed rates found within 10km of selected locations"
                }
              </p>
            </div>
          )}

          {filteredRates.length > 0 && (
            <div className="space-y-3">
              {filteredRates.map((rate) => (
                <div
                  key={rate.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleRateSelect(rate)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {/* Route Display */}
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-green-600" />
                          <span className="font-medium">{rate.pickup_area.name}</span>
                          {rate.pickup_area.type !== "POINT" && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                              {getAreaTypeIcon(rate.pickup_area.type)}
                            </Badge>
                          )}
                        </div>
                        
                        <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                        
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-red-600" />
                          <span className="font-medium">{rate.dropoff_area.name}</span>
                          {rate.dropoff_area.type !== "POINT" && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                              {getAreaTypeIcon(rate.dropoff_area.type)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Price Display and Action Button */}
                    <div className="text-right ml-4 flex items-center gap-3">
                      <div className="text-lg font-semibold flex items-center gap-1">
                        <span>{rate.price}</span>
                        <Badge variant="secondary" className="text-xs">
                          {rate.currency}
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRateSelect(rate)
                        }}
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}